import { z } from 'zod';
import { apiClient } from '../lib/api';
import { ApiResponseSchema, PaginatedApiResponseSchema } from '../schemas';

const SystemStatsSchema = z.object({
	totalUsers: z.number(),
	totalStudents: z.number(),
	totalFaculties: z.number(),
	totalMajors: z.number()
}).strip();

const CacheStatusSchema = z.object({
	redis_available: z.boolean(),
	timestamp: z.string()
}).strip();

const CacheResetResponseSchema = z.object({
	redis_available: z.boolean(),
	timestamp: z.string(),
	warm_caches_executed: z.boolean(),
	warmed_caches: z.array(z.string()).optional(),
	warming_errors: z.array(z.string()).optional()
}).strip();

const ExportableTableSchema = z.object({
	key: z.string(),
	name: z.string(),
	count: z.number().optional()
}).strip();

const ExportTablesResponseSchema = z.object({
	tables: z.array(ExportableTableSchema),
	formats: z.array(z.string())
}).strip();

const BackendLogItemSchema = z.object({
	time: z.string().nullable(),
	level: z.string(),
	message: z.string(),
	raw: z.string()
}).strip();

const BackendLogsResponseSchema = PaginatedApiResponseSchema(z.array(BackendLogItemSchema));

export type SystemStats = z.infer<typeof SystemStatsSchema>;
export type CacheStatus = z.infer<typeof CacheStatusSchema>;
export type CacheResetResponse = z.infer<typeof CacheResetResponseSchema>;
export type ExportableTable = z.infer<typeof ExportableTableSchema>;
export type ExportTablesResponse = z.infer<typeof ExportTablesResponseSchema>;
export type BackendLogItem = z.infer<typeof BackendLogItemSchema>;
export type BackendLogsResponse = z.infer<typeof BackendLogsResponseSchema>;
export type ExportFormat = 'excel' | 'csv';

export interface BackendLogQuery {
	q?: string;
	level?: string;
	from?: string;
	to?: string;
	page?: number;
	limit?: number;
}

export const adminApi = {
	getSystemStats: async () => {
		const response = await apiClient.get('/admin/stats');
		return ApiResponseSchema(SystemStatsSchema).parse(response.data).data;
	},

	getCacheStatus: async () => {
		const response = await apiClient.get('/admin/cache/status');
		return ApiResponseSchema(CacheStatusSchema).parse(response.data).data;
	},

	resetCache: async (warmCaches: boolean = true) => {
		const response = await apiClient.post('/admin/cache/reset', { warmCaches });
		return ApiResponseSchema(CacheResetResponseSchema).parse(response.data).data;
	},

	getBackendLogs: async (query: BackendLogQuery = {}) => {
		const params = new URLSearchParams();
		Object.entries(query).forEach(([key, value]) => {
			if (value !== undefined && value !== null && value !== '') {
				params.set(key, String(value));
			}
		});
		const response = await apiClient.get(`/admin/logs?${params.toString()}`);
		return BackendLogsResponseSchema.parse(response.data);
	},

	getExportableTables: async () => {
		const response = await apiClient.get('/admin/export/tables');
		return ApiResponseSchema(ExportTablesResponseSchema).parse(response.data).data;
	},

	exportTable: async (
		tableName: string,
		format: ExportFormat,
		onProgress?: (bytesReceived: number) => void,
		includeId?: boolean
	): Promise<void> => {
		// `includeId` mặc định tắt — chỉ gửi khi bật, giữ đúng hợp đồng export hiện tại.
		const params = includeId ? { includeId: 'true' } : undefined;
		return apiClient.downloadBlob(`/admin/export/${tableName}/${format}`, params, undefined, onProgress);
	},

	downloadBackup: (): Promise<void> => {
		return apiClient.downloadBlob('/admin/backup/download');
	},

	restoreBackup: async (file: File): Promise<void> => {
		const formData = new FormData();
		formData.append('file', file);
		await apiClient.raw.post('/admin/backup/restore', formData, {
			timeout: 300000, // 5 minutes for large restores
		});
	}
};
